import { Client, PrivateKey, cryptoUtils } from '@hiveio/dhive';
import { postsApi } from './api';
import HIVE_RPC_NODES from '../config/hiveNodes';
import { extractKeychainErrorMessage, isPlainEmptyObject } from '../utils/keychainErrors';

const APP_NAME = 'flexus';
const DEFAULT_TAG = 'flexus';
const SIGNATURE_SHORT_POSTS_KEY_PREFIX = 'app-signature-short-posts-';
const SIGNATURE_ARTICLES_KEY_PREFIX = 'app-signature-articles-';

// These should match your backend configuration
const TARGET_POSTS = [
  {
    author: 'hive-144726',
    permlink: 'published-test'
  }
];

// Hive blockchain client
const client = new Client(HIVE_RPC_NODES);

const isLikelyImageUrl = (url = '') => {
  const value = String(url || '').trim();
  if (!/^https?:\/\//i.test(value)) return false;
  if (/\.(?:jpg|jpeg|png|gif|webp|svg)(?:$|[?#])/i.test(value)) return true;

  try {
    const parsed = new URL(value);
    const host = parsed.hostname.toLowerCase();
    const path = parsed.pathname.toLowerCase();

    if (host === 'images.hive.blog' && (path.startsWith('/p/') || path.startsWith('/0x0/') || path.startsWith('/300x0/'))) {
      return true;
    }
  } catch {
    return false;
  }

  return false;
};

class HiveService {
  constructor() {
    this.loginMethod = null; // 'keychain' or 'postingkey'
    this.postingKey = null; // Decrypted posting key (only in memory during session)
    this.signatureSettingsCache = new Map();
  }

  notifyPostingKeyInvalid(username, errorMsg) {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('hive:posting-key-invalid', {
        detail: { username, error: errorMsg }
      }));
    }
  }

  isAuthError(err) {
    const msg = (err && (err.message || String(err))) || '';
    return msg.includes('missing required posting authority') ||
           msg.includes('Missing Posting Authority') ||
           msg.includes('irrelevant signature') ||
           msg.includes('unknown key') ||
           msg.includes('Invalid posting key');
  }

  async getUserSignatureSettings(username, forceRefresh = false) {
    const user = String(username || '').trim();
    if (!user) {
      return { signature: '', signatureOnShortPosts: false, signatureOnArticles: false };
    }

    const cacheKey = user.toLowerCase();
    const cached = this.signatureSettingsCache.get(cacheKey);
    const cacheTtlMs = 60000;

    if (!forceRefresh && cached && (Date.now() - cached.ts) < cacheTtlMs) {
      return cached.data;
    }

    const fallback = {
      signatureFlux: typeof window !== 'undefined' ? (localStorage.getItem(`app-signature-flux-${user}`) || localStorage.getItem(`app-signature-${user}`) || '') : '',
      signatureFluxEnabled: typeof window !== 'undefined'
        ? localStorage.getItem(`app-signature-flux-enabled-${user}`) === 'true' || localStorage.getItem(`${SIGNATURE_ARTICLES_KEY_PREFIX}${user}`) === 'true'
        : false,
      signatureFlex: typeof window !== 'undefined' ? (localStorage.getItem(`app-signature-flex-${user}`) || localStorage.getItem(`app-signature-${user}`) || '') : '',
      signatureFlexEnabled: typeof window !== 'undefined'
        ? localStorage.getItem(`app-signature-flex-enabled-${user}`) === 'true' || localStorage.getItem(`${SIGNATURE_SHORT_POSTS_KEY_PREFIX}${user}`) === 'true'
        : false,
      signatureComments: typeof window !== 'undefined' ? (localStorage.getItem(`app-signature-comments-${user}`) || localStorage.getItem(`app-signature-${user}`) || '') : '',
      signatureCommentsEnabled: typeof window !== 'undefined'
        ? localStorage.getItem(`app-signature-comments-enabled-${user}`) !== 'false'
        : true,
    };

    try {
      const response = await fetch(`/api/user/${encodeURIComponent(user)}/settings`);
      if (!response.ok) {
        this.signatureSettingsCache.set(cacheKey, { ts: Date.now(), data: fallback });
        return fallback;
      }

      const data = await response.json();
      const settings = data?.settings || {};
      const resolved = {
        signatureFlux: settings.signatureFlux !== undefined ? settings.signatureFlux : (settings.signature || fallback.signatureFlux),
        signatureFluxEnabled: typeof settings.signatureFluxEnabled === 'boolean' ? settings.signatureFluxEnabled : (typeof settings.signatureOnArticles === 'boolean' ? settings.signatureOnArticles : fallback.signatureFluxEnabled),
        signatureFlex: settings.signatureFlex !== undefined ? settings.signatureFlex : (settings.signature || fallback.signatureFlex),
        signatureFlexEnabled: typeof settings.signatureFlexEnabled === 'boolean' ? settings.signatureFlexEnabled : (typeof settings.signatureOnShortPosts === 'boolean' ? settings.signatureOnShortPosts : fallback.signatureFlexEnabled),
        signatureComments: settings.signatureComments !== undefined ? settings.signatureComments : (settings.signature || fallback.signatureComments),
        signatureCommentsEnabled: typeof settings.signatureCommentsEnabled === 'boolean' ? settings.signatureCommentsEnabled : fallback.signatureCommentsEnabled,
      };

      if (typeof window !== 'undefined') {
        localStorage.setItem(`app-signature-flux-${user}`, resolved.signatureFlux || '');
        localStorage.setItem(`app-signature-flux-enabled-${user}`, String(!!resolved.signatureFluxEnabled));
        localStorage.setItem(`app-signature-flex-${user}`, resolved.signatureFlex || '');
        localStorage.setItem(`app-signature-flex-enabled-${user}`, String(!!resolved.signatureFlexEnabled));
        localStorage.setItem(`app-signature-comments-${user}`, resolved.signatureComments || '');
        localStorage.setItem(`app-signature-comments-enabled-${user}`, String(!!resolved.signatureCommentsEnabled));
      }

      this.signatureSettingsCache.set(cacheKey, { ts: Date.now(), data: resolved });
      return resolved;
    } catch {
      this.signatureSettingsCache.set(cacheKey, { ts: Date.now(), data: fallback });
      return fallback;
    }
  }

  async resolveSignatureOptions(username, context = 'comment', options = {}) {
    const settings = await this.getUserSignatureSettings(username);
    
    let signature;
    let applySignature;

    if (context === 'article') {
      signature = options.signature !== undefined ? options.signature : settings.signatureFlux;
      applySignature = typeof options.applySignature === 'boolean' ? options.applySignature : !!settings.signatureFluxEnabled;
    } else if (context === 'short') {
      signature = options.signature !== undefined ? options.signature : settings.signatureFlex;
      applySignature = typeof options.applySignature === 'boolean' ? options.applySignature : !!settings.signatureFlexEnabled;
    } else { // comment
      signature = options.signature !== undefined ? options.signature : settings.signatureComments;
      applySignature = typeof options.applySignature === 'boolean' ? options.applySignature : !!settings.signatureCommentsEnabled;
    }

    return { signature, applySignature };
  }

  async searchAccounts(prefix, limit = 10) {
    const normalizedPrefix = String(prefix || '').trim().toLowerCase();
    const normalizedLimit = Math.max(1, Math.min(50, Number(limit) || 10));
    if (normalizedPrefix.length < 3) return [];

    try {
      const result = await client.call('condenser_api', 'lookup_accounts', [normalizedPrefix, normalizedLimit]);
      const names = Array.isArray(result) ? result : [];
      return Array.from(new Set(
        names
          .map((name) => String(name || '').toLowerCase())
          .filter((name) => name && name.startsWith(normalizedPrefix))
      )).slice(0, normalizedLimit);
    } catch (error) {
      console.error('searchAccounts failed:', error?.message || error);
      return [];
    }
  }

  // Extract images from markdown body
  extractImages(body) {
    const images = [];
    // Match markdown images: ![alt](url)
    const markdownImageRegex = /!\[.*?\]\((https?:\/\/[^\s)]+)\)/g;
    let match;
    while ((match = markdownImageRegex.exec(body)) !== null) {
      images.push(match[1]);
    }
    // Match plain image URLs
    const plainImageRegex = /(https?:\/\/[^\s]+)/gi;
    while ((match = plainImageRegex.exec(body)) !== null) {
      const url = match[1].replace(/['")\\]>\\]+$/, '');
      if (isLikelyImageUrl(url) && !images.includes(url)) {
        images.push(url);
      }
    }

    // Match shortcode images: [img]https://...[/img]
    const shortcodeImageRegex = /\[img\]([\s\S]*?)\[\/img\]/gi;
    while ((match = shortcodeImageRegex.exec(body)) !== null) {
      const url = String(match[1] || '').trim();
      if (url && !images.includes(url)) {
        images.push(url);
      }
    }

    // Legacy shortcode support: [img url=...] and [img src=...]
    const legacyShortcodeImageRegex = /\[img\s+[^\]]*(?:url|src)\s*=\s*(?:"([^"]+)"|([^\s\]]+))[^\]]*\]/gi;
    while ((match = legacyShortcodeImageRegex.exec(body)) !== null) {
      const url = (match[1] || match[2] || '').trim();
      if (url && !images.includes(url)) {
        images.push(url);
      }
    }

    // Match HTML image tags: <img ... src="https://..."> (supports single/double/no quotes)
    const htmlImageRegex = /<img\b[^>]*\bsrc\s*=\s*(?:"([^"]+)"|'([^']+)'|([^\s>]+))/gi;
    while ((match = htmlImageRegex.exec(body)) !== null) {
      const url = (match[1] || match[2] || match[3] || '').trim();
      if (isLikelyImageUrl(url) && !images.includes(url)) {
        images.push(url);
      }
    }

    return images;
  }

  // Keep broadcast body Hive-client-friendly by converting image shortcodes
  // into plain URLs immediately before sending to chain.
  normalizeBodyForBroadcast(body) {
    if (!body) return body;

    return String(body)
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  // Extract links from markdown body
  extractLinks(body) {
    const links = [];
    // Match markdown links: [text](url)
    const markdownLinkRegex = /\[.*?\]\((https?:\/\/[^\s)]+)\)/g;
    let match;
    while ((match = markdownLinkRegex.exec(body)) !== null) {
      links.push(match[1]);
    }
    // Match plain URLs (http/https)
    const plainUrlRegex = /(?:^|[\s])(https?:\/\/[^\s]+)/gi;
    while ((match = plainUrlRegex.exec(body)) !== null) {
      const url = match[1];
      // Exclude image URLs
      if (!url.match(/\.(?:jpg|jpeg|png|gif|webp|svg)$/i) && !links.includes(url)) {
        links.push(url);
      }
    }
    return links;
  }

  // Check if Hive Keychain extension is available
  isKeychainInstalled() {
    return typeof window.hive_keychain !== 'undefined';
  }

  // Set login method and posting key (for posting key login)
  setLoginMethod(method, postingKey = null) {
    this.loginMethod = method;
    this.postingKey = postingKey;
  }

  // Get current login method
  getLoginMethod() {
    return this.loginMethod;
  }

  // Get posting key from memory or sessionStorage (for Web2 non-custodial users)
  getPostingKey() {
    return this.postingKey || sessionStorage.getItem('hive_posting_key');
  }

  // Verify posting key is valid for the account
  async verifyPostingKey(username, postingKey) {
    try {
      // Try direct RPC lookup first (fast when CORS is allowed)
      try {
        const account = await client.database.getAccounts([username]);
        if (account && account.length > 0) {
          const publicKey = account[0].posting.key_auths[0][0];
          const privateKey = PrivateKey.fromString(postingKey);
          const derivedPublicKey = privateKey.createPublic().toString();
          if (publicKey === derivedPublicKey) return true;
          throw new Error('Invalid posting key for this account');
        }
      } catch (err) {
        // Fallback: derive public key locally and ask our server to verify (avoids CORS issues)
        try {
          const privateKey = PrivateKey.fromString(postingKey);
          const derivedPublicKey = privateKey.createPublic().toString();
          const response = await fetch('/api/hive/verify-posting-key', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, publicKey: derivedPublicKey })
          });
          const data = await response.json();
          if (response.ok && data && data.ok) return true;
          throw new Error('Invalid posting key for this account (verified via server)');
        } catch (fallbackErr) {
          throw new Error(fallbackErr.message || 'Failed to verify posting key');
        }
      }

      throw new Error('Account not found');
    } catch (error) {
      throw new Error(error.message || 'Failed to verify posting key');
    }
  }

  // Sign image data for upload to images.hive.blog
  async signImageUpload(username, imageArrayBuffer) {
    const prefix = new TextEncoder().encode('ImageSigningChallenge');
    const challengeBuffer = new Uint8Array(prefix.length + imageArrayBuffer.byteLength);
    challengeBuffer.set(prefix, 0);
    challengeBuffer.set(new Uint8Array(imageArrayBuffer), prefix.length);

    const hashBuffer = cryptoUtils.sha256(challengeBuffer);

    if (this.loginMethod === 'postingkey' || this.loginMethod === 'web2') {
      const postingKey = this.getPostingKey();
      if (!postingKey) throw new Error('No posting key available for image signing');
      const privateKey = PrivateKey.fromString(postingKey);
      const signature = privateKey.sign(hashBuffer);
      return signature.toString();
    }

    // Keychain path
    if (!this.isKeychainInstalled()) {
      throw new Error('Hive Keychain is required to upload images');
    }

    const message = '{"type":"Buffer","data":[' + challengeBuffer.join(',') + ']}';

    return new Promise((resolve, reject) => {
      window.hive_keychain.requestSignBuffer(
        username,
        message,
        'Posting',
        (response) => {
          if (response.success) {
            resolve(response.result);
          } else {
            reject(new Error(response.message || 'Image signing failed'));
          }
        }
      );
    });
  }

  // Upload image to images.hive.blog
  async uploadImageToHive(username, file) {
    const buffer = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Failed to read file buffer'));
      reader.readAsArrayBuffer(file);
    });
    const signature = await this.signImageUpload(username, buffer);

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(
      `https://images.hive.blog/${encodeURIComponent(username)}/${signature}`,
      { method: 'POST', body: formData }
    );

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new Error(`Image upload failed: ${errorText}`);
    }

    const data = await response.json();
    return data.url;
  }

  // Request account from Keychain - with verification
  async requestKeychainLogin() {
    if (!this.isKeychainInstalled()) {
      throw new Error('Hive Keychain extension not installed. Please install it from https://hive-keychain.com/');
    }

    return new Promise((resolve, reject) => {
      // Request sign buffer to verify account ownership (triggers Keychain popup)
      window.hive_keychain.requestSignBuffer(
        null, // Let user choose account from Keychain
        'Login to Flexus',
        'Posting',
        (response) => {
          if (response.success) {
            // Extract username from response
            const username = response.data.username;
            this.setLoginMethod('keychain');
            resolve(username);
          } else {
            reject(new Error(response.message || 'Keychain login cancelled or failed'));
          }
        }
      );
    });
  }

  // Broadcast a custom_json operation using posting key or Keychain
  async broadcastCustomJson(username, id, jsonPayload) {
    if (this.loginMethod === 'postingkey') {
      const postingKey = this.getPostingKey();
      if (!postingKey) throw new Error('No posting key available');
      try {
        const privateKey = PrivateKey.fromString(postingKey);
        const op = ['custom_json', { id: id, required_auths: [], required_posting_auths: [username], json: JSON.stringify(jsonPayload) }];
        const result = await client.broadcast.sendOperations([op], privateKey);
        return result;
      } catch (err) {
        if (this.isAuthError(err)) {
          this.notifyPostingKeyInvalid(username, err.message);
        }
        throw new Error(err.message || 'Failed to broadcast custom json');
      }
    } else {
      // Use Keychain
      return new Promise((resolve, reject) => {
        if (!this.isKeychainInstalled()) return reject(new Error('Keychain not installed'));
        window.hive_keychain.requestCustomJson(username, id, 'Posting', JSON.stringify(jsonPayload), 'Update last read', (resp) => {
          if (resp && resp.success) resolve(resp);
          else reject(new Error(resp?.message || 'Keychain request failed'));
        });
      });
    }
  }

  /**
   * Update account profile metadata on the Hive blockchain.
   *
   * "Update Flexus" (proflex) is always written — it is the source of truth for this app.
   * scope.mostHive → also writes posting_json_metadata.profile (v2) + flat top-level fields
   * scope.allHive  → also writes json_metadata (requires Keychain active key)
   */
  async updateProfile(username, profileData, scope = {}) {
    const { mostHive = false, allHive = false } = scope;

    // Fetch current account to safely merge non-profile fields
    let account;
    try {
      const accounts = await client.database.getAccounts([username]);
      account = Array.isArray(accounts) && accounts.length > 0 ? accounts[0] : null;
    } catch (err) {
      throw new Error('Failed to fetch account: ' + (err.message || err));
    }
    if (!account) throw new Error('Account not found');

    const safeJson = (raw) => {
      try { return typeof raw === 'string' ? JSON.parse(raw || '{}') : raw || {}; }
      catch { return {}; }
    };

    // Keep raw on-chain strings so we never silently mutate values we aren't touching
    // (e.g. "" must stay "" — re-serialising to "{}" would be a dirty write)
    const rawLegacyStr  = typeof account.json_metadata === 'string' ? account.json_metadata : '';
    const rawPostingStr = typeof account.posting_json_metadata === 'string' ? account.posting_json_metadata : '';

    const currentPosting = safeJson(rawPostingStr);
    const currentLegacy  = safeJson(rawLegacyStr);

    // Canonical profile data object
    const proflex = {
      name:          profileData.name         || '',
      about:         profileData.about        || '',
      profile_image: profileData.profileImage || '',
      cover_image:   profileData.coverImage   || '',
      website:       profileData.website      || '',
      location:      profileData.location     || '',
    };

    // Include default_beneficiaries in proflex if provided
    if (Array.isArray(profileData.defaultBeneficiaries) && profileData.defaultBeneficiaries.length > 0) {
      proflex.default_beneficiaries = profileData.defaultBeneficiaries
        .map(t => t.split('=').map(s => s.trim()))
        .filter(p => p.length === 2 && p[0] && p[1])
        .map(([account, w]) => ({ account, weight: parseInt(w, 10) || 0 }))
        .filter(b => b.weight > 0);
    } else if (profileData.defaultBeneficiaries !== undefined) {
      // Explicitly cleared
      proflex.default_beneficiaries = [];
    }

    // ── Flexus-only (default) ───────────────────────────────────────────────
    // Inject proflex at the top level of posting_json_metadata.
    // Every other field — profile, tokens, badges, app keys — is passed through
    // byte-for-byte from the chain so no other frontend is affected.
    const newPostingMeta = {
      ...currentPosting,
      proflex,
    };

    // Clean up the old nested location (posting_json_metadata.profile.proflex)
    // in case it was written by a previous version of this app.
    if (newPostingMeta.profile?.proflex !== undefined) {
      const { proflex: _old, ...profileWithoutProflex } = newPostingMeta.profile;
      newPostingMeta.profile = profileWithoutProflex;
    }

    if (mostHive || allHive) {
      // ── mostHive ────────────────────────────────────────────────────────
      // Merge profile fields into the existing profile object so tokens/badges
      // and any other app-specific keys are preserved.
      // proflex is already removed from profile above; standard fields are updated here.
      // default_beneficiaries is intentionally excluded — it lives in proflex only.
      const { default_beneficiaries: _db, ...proflexPublic } = proflex;
      newPostingMeta.profile       = { ...(newPostingMeta.profile || {}), ...proflexPublic, version: 2 };
      newPostingMeta.name          = proflex.name;
      newPostingMeta.about         = proflex.about;
      newPostingMeta.profile_image = proflex.profile_image;
      newPostingMeta.cover_image   = proflex.cover_image;
      newPostingMeta.website       = proflex.website;
      newPostingMeta.location      = proflex.location;
    }

    if (allHive) {
      // ── allHive ─────────────────────────────────────────────────────────
      // Active key operation — Keychain only.
      // Also updates json_metadata; merge into existing to avoid wiping it.
      // default_beneficiaries is intentionally excluded — it lives in proflex only.
      if (!this.isKeychainInstalled()) {
        throw new Error('Hive Keychain is required for "Update all Hive" (active key needed)');
      }
      const { default_beneficiaries: _db2, ...proflexPublic2 } = proflex;
      const newLegacyMeta = { ...currentLegacy, profile: { ...proflexPublic2, version: 2 } };
      const ops = [['account_update2', {
        account: username,
        json_metadata: JSON.stringify(newLegacyMeta),
        posting_json_metadata: JSON.stringify(newPostingMeta),
        extensions: [],
      }]];
      return new Promise((resolve, reject) => {
        window.hive_keychain.requestBroadcast(username, ops, 'active', (response) => {
          console.log('[updateProfile] Keychain active response:', response);
          if (response && response.success) resolve(response);
          else reject(new Error(response?.message || response?.error || JSON.stringify(response) || 'Keychain active broadcast failed'));
        });
      });
    }

    // ── Flexus / mostHive posting-key broadcast ─────────────────────────────
    // json_metadata must be "" (empty string) on the posting-key path.
    // The blockchain only requires the active key when json_metadata is non-empty.
    // Passing "" means "leave json_metadata unchanged" — it does NOT clear it.
    // This matches how PeakD handles posting-key profile updates.
    const ops = [['account_update2', {
      account: username,
      json_metadata: '',
      posting_json_metadata: JSON.stringify(newPostingMeta),
      extensions: [],
    }]];

    const hasSessionPostingKey = !!sessionStorage.getItem('hive_posting_key');
    const shouldUsePostingKey = this.loginMethod === 'postingkey' || (!this.loginMethod && hasSessionPostingKey);

    if (shouldUsePostingKey) {
      const postingKey = this.getPostingKey();
      if (!postingKey) throw new Error('No posting key available');
      try {
        const privateKey = PrivateKey.fromString(postingKey);
        return await client.broadcast.sendOperations(ops, privateKey);
      } catch (err) {
        throw new Error(err.message || 'Failed to update profile');
      }
    }

    return new Promise((resolve, reject) => {
      if (!this.isKeychainInstalled()) {
        return reject(new Error('Hive Keychain extension not installed'));
      }
      window.hive_keychain.requestBroadcast(username, ops, 'posting', (response) => {
        console.log('[updateProfile] Keychain posting response:', response);
        if (response && response.success) resolve(response);
        else reject(new Error(response?.message || response?.error || JSON.stringify(response) || 'Keychain broadcast failed'));
      });
    });
  }

  /**
   * Update only default_beneficiaries in posting_json_metadata.proflex.
   * Used for silent background seeding on first login (posting key only — skips keychain).
   * beneficiaries: array of { account, weight } objects.
   */
  async updateProflexDefaultBeneficiaries(username, beneficiaries) {
    const hasPostingKey = this.loginMethod === 'postingkey' || !!sessionStorage.getItem('hive_posting_key');

    // Keychain: skip silent background seeding to avoid an unexpected second prompt.
    // Proflex will be written the first time the user saves their profile at settings/profile.
    if (!hasPostingKey) {
      return { skipped: true };
    }

    let account;
    try {
      const accounts = await client.database.getAccounts([username]);
      account = Array.isArray(accounts) && accounts.length > 0 ? accounts[0] : null;
    } catch { return { skipped: true }; }
    if (!account) return { skipped: true };

    const safeJson = (raw) => {
      try { return typeof raw === 'string' ? JSON.parse(raw || '{}') : raw || {}; }
      catch { return {}; }
    };
    const currentPosting = safeJson(account.posting_json_metadata);
    const existingProflex = currentPosting.proflex || {};

    // Skip if already set with the same values
    const existing = existingProflex.default_beneficiaries;
    if (Array.isArray(existing) && existing.length === beneficiaries.length &&
        beneficiaries.every((b, i) => existing[i]?.account === b.account && existing[i]?.weight === b.weight)) {
      return { skipped: true, reason: 'already set' };
    }

    const updatedPosting = {
      ...currentPosting,
      proflex: { ...existingProflex, default_beneficiaries: beneficiaries },
    };

    const ops = [['account_update2', {
      account: username,
      json_metadata: '',
      posting_json_metadata: JSON.stringify(updatedPosting),
      extensions: [],
    }]];

    try {
      const postingKey = this.getPostingKey();
      if (!postingKey) return { skipped: true };
      const pk = PrivateKey.fromString(postingKey);
      return await client.broadcast.sendOperations(ops, pk);
    } catch (err) {
      console.warn('[hiveService] updateProflexDefaultBeneficiaries failed silently:', err.message);
      return { skipped: true, error: err.message };
    }
  }

  /**
   * Write a proflex_backup custom_json — completely separate from account_update2 so no
   * other frontend can accidentally clobber it when they save profile fields.
   * Called silently every time proflex is successfully written to the chain.
   * @param {string} username
   * @param {Object} proflex - the same proflex object that was just saved
   */
  async writeProflexBackup(username, proflex) {
    if (!username || !proflex) return { skipped: true };
    const payload = { v: 1, proflex };
    try {
      await this.broadcastCustomJson(username, 'proflex_backup', payload);
      console.log('[hiveService] proflex_backup written for @' + username);
      return { ok: true };
    } catch (err) {
      // Non-critical — swallow silently
      console.warn('[hiveService] writeProflexBackup failed (non-critical):', err.message);
      return { skipped: true, error: err.message };
    }
  }

  /**
   * Check whether posting_json_metadata.proflex is missing and, if so, silently restore
   * it from posting_json_metadata.profile / flat fields (written by other frontends).
   * Only runs with a posting key — skips for keychain to avoid unexpected prompts.
   * @param {string} username
   * @returns {{ restored: boolean, skipped: boolean }}
   */
  async checkAndRestoreProflex(username) {
    const hasPostingKey = this.loginMethod === 'postingkey' || !!sessionStorage.getItem('hive_posting_key');
    if (!hasPostingKey) return { skipped: true, reason: 'no posting key' };

    let account;
    try {
      const accounts = await client.database.getAccounts([username]);
      account = Array.isArray(accounts) && accounts.length > 0 ? accounts[0] : null;
    } catch { return { skipped: true, reason: 'account fetch failed' }; }
    if (!account) return { skipped: true, reason: 'account not found' };

    const safeJson = (raw) => {
      try { return typeof raw === 'string' ? JSON.parse(raw || '{}') : raw || {}; }
      catch { return {}; }
    };

    const currentPosting = safeJson(account.posting_json_metadata);

    // If proflex is present and has meaningful content, nothing to do
    if (currentPosting.proflex && (currentPosting.proflex.name || currentPosting.proflex.profile_image)) {
      return { restored: false, skipped: true, reason: 'proflex intact' };
    }

    // Try to recover from posting_json_metadata.profile (PeakD / Ecency style) or flat fields
    const profile = currentPosting.profile || {};
    const name          = profile.name          || currentPosting.name          || '';
    const about         = profile.about         || currentPosting.about         || '';
    const profile_image = profile.profile_image || currentPosting.profile_image || '';
    const cover_image   = profile.cover_image   || currentPosting.cover_image   || '';
    const website       = profile.website       || currentPosting.website       || '';
    const location      = profile.location      || currentPosting.location      || '';

    // Nothing worth recovering
    if (!name && !about && !profile_image) return { restored: false, skipped: true, reason: 'no data to recover' };

    const recoveredProflex = {
      name, about, profile_image, cover_image, website, location,
      // Preserve existing default_beneficiaries if they existed
      ...(currentPosting.proflex?.default_beneficiaries
        ? { default_beneficiaries: currentPosting.proflex.default_beneficiaries }
        : {}),
    };

    const updatedPosting = { ...currentPosting, proflex: recoveredProflex };

    const ops = [['account_update2', {
      account: username,
      json_metadata: '',
      posting_json_metadata: JSON.stringify(updatedPosting),
      extensions: [],
    }]];

    try {
      const postingKey = this.getPostingKey();
      if (!postingKey) return { skipped: true, reason: 'no posting key in session' };
      const pk = PrivateKey.fromString(postingKey);
      await client.broadcast.sendOperations(ops, pk);
      console.log(`[hiveService] proflex restored for @${username} from profile/flat fields`);
      // Also write custom_json backup now that we have fresh data
      this.writeProflexBackup(username, recoveredProflex).catch(() => {});
      return { restored: true };
    } catch (err) {
      console.warn('[hiveService] checkAndRestoreProflex write failed:', err.message);
      return { skipped: true, error: err.message };
    }
  }

  /**
   * Get latest container post for a feed type
   */
  async getLatestContainer(feedType) {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/posts/latest-container/${feedType}`);
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }
      const data = await response.json();
      return data.container;
    } catch (error) {
      console.error('Error fetching latest container:', error);
      throw error;
    }
  }

  /**
   * Build payload for short post creation (target container + metadata)
   */
  async buildShortPostPayload(username, body, tags = [], feedType = null, permlinkOverride = null, targetPostOverride = null) {
    let permlink;
    let targetPost = targetPostOverride;
    let jsonMetadata;

    // Determine target post and metadata based on feed type
    if (feedType && feedType !== 'all') {
      // Get latest container post for the specific feed
      if (!targetPost) {
        targetPost = await this.getLatestContainer(feedType);
      }
      
      if (!targetPost) {
        throw new Error(`No container post found for ${feedType}`);
      }

      // Special metadata for threads
      if (feedType === 'threads') {
        permlink = permlinkOverride || this.generateThreadsPermlink(targetPost.author);
        jsonMetadata = this.buildThreadsMetadata(username, permlink, body);
      } else {
        // Standard metadata for other feeds
        permlink = permlinkOverride || this.generatePermlink();
        jsonMetadata = {
          app: `${APP_NAME}/1.0.0`,
          tags: [DEFAULT_TAG, ...tags].slice(0, 5),
          format: 'markdown'
        };
      }
    } else {
      // Default container
      permlink = permlinkOverride || this.generatePermlink();
      targetPost = targetPost || TARGET_POSTS[0];
      jsonMetadata = {
        app: `${APP_NAME}/1.0.0`,
        tags: [DEFAULT_TAG, ...tags].slice(0, 5),
        format: 'markdown'
      };
    }

    return { permlink, targetPost, jsonMetadata };
  }

  /**
   * Build metadata for comments/replies
   */
  /**
   * Build JSON metadata for a Threads (leothreads) post or reply.
   * @param {string} username
   * @param {string} permlink
   * @param {string} body        - final broadcast body (used to extract links/images)
   * @param {object} [opts]
   * @param {string[]} [opts.tags]   - extra tags merged after 'leofinance' (sliced to 5 total)
   * @param {object}  [opts.extra]   - additional top-level fields merged into the result
   */
  // Tags used for Threads posts — edit here to change
  static THREADS_TAGS = ['leofinance'];

  buildThreadsTitle(username, body) {
    const clean = body.replace(/[\r\n]+/g, ' ').substring(0, 30);
    return `@${username} "${clean}..."`;
  }

  buildThreadsCommentOptions(username, permlink) {
    return ['comment_options', {
      author: username,
      permlink,
      max_accepted_payout: '1000000.000 HBD',
      percent_hbd: 10000,
      allow_votes: true,
      allow_curation_rewards: true,
      extensions: [],
    }];
  }

  buildThreadsMetadata(username, permlink, body, { extra = {} } = {}) {
    const links = this.extractLinks(body);
    const images = this.extractImages(body);
    return {
      app: 'leothreads/0.3',
      format: 'markdown',
      tags: HiveService.THREADS_TAGS,
      canonical_url: `https://inleo.io/threads/view/${username}/${permlink}`,
      ...(links.length ? { links } : {}),
      ...(images.length ? { images } : {}),
      isPoll: false,
      pollOptions: {},
      dimensions: {},
      ...extra,
    };
  }

  buildCommentMetadata(username, body, parentAuthor, rootParentAuthor, permlink) {
    if (rootParentAuthor === 'leothreads' || parentAuthor === 'leothreads') {
      return this.buildThreadsMetadata(username, permlink, body);
    }

    return {
      app: `${APP_NAME}/1.0.0`,
      format: 'markdown'
    };
  }

  appendUserSignature(body, username, signatureOverride = null) {
    const defaultSignature = typeof window !== 'undefined' ? localStorage.getItem(`app-signature-${username}`) : '';
    const signature = (signatureOverride !== null && signatureOverride !== undefined ? signatureOverride : defaultSignature) || '';
    const text = signature.toString().trim();

    if (!text) {
      return body;
    }

    const bodyText = (body || '').trimEnd();
    const signatureBlock = `--- \n\n${text}`;

    if (bodyText.endsWith(signatureBlock)) {
      return bodyText;
    }

    // Avoid duplicating if user already included exact signature block
    if (bodyText.includes(`\n\n${signatureBlock}`)) {
      return bodyText;
    }

    return `${bodyText}${bodyText ? '\n\n' : ''}${signatureBlock}`;
  }

  getSignatureContextEnabled(username, context = 'comment', enabledOverride = null) {
    if (typeof enabledOverride === 'boolean') {
      return enabledOverride;
    }

    if (typeof window === 'undefined' || !username) {
      return false;
    }

    if (context === 'comment') {
      const stored = localStorage.getItem(`app-signature-comments-enabled-${username}`);
      return stored !== null ? stored === 'true' : true;
    }

    if (context === 'short') {
      return localStorage.getItem(`app-signature-flex-enabled-${username}`) === 'true';
    }

    if (context === 'article') {
      return localStorage.getItem(`app-signature-flux-enabled-${username}`) === 'true';
    }

    return false;
  }

  appendUserSignatureForContext(body, username, context = 'comment', signatureOverride = null, enabledOverride = null) {
    if (!this.getSignatureContextEnabled(username, context, enabledOverride)) {
      return body;
    }

    let signature = signatureOverride;
    if (signature === null || signature === undefined) {
      if (typeof window !== 'undefined') {
        if (context === 'comment') {
          signature = localStorage.getItem(`app-signature-comments-${username}`);
        } else if (context === 'short') {
          signature = localStorage.getItem(`app-signature-flex-${username}`);
        } else if (context === 'article') {
          signature = localStorage.getItem(`app-signature-flux-${username}`);
        }
      }
    }

    return this.appendUserSignature(body, username, signature);
  }

  /**
   * Send optimistic post data to the backend for instant feed rendering
   */
  async _sendOptimisticPost(data) {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      await fetch(`${apiUrl}/posts/optimistic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
    } catch (err) {
      console.warn('Failed to send optimistic post to backend', err);
    }
  }

  /**
   * Send optimistic vote to the backend for instant feed rendering
   */
  async _sendOptimisticVote(author, permlink, weight) {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      await fetch(`${apiUrl}/feed/optimistic-vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ author, permlink, weight })
      });
    } catch (err) {
      console.warn('Failed to send optimistic vote to backend', err);
    }
  }

  /**
   * Create a new short post (as a comment on a target post)
   */
  async createPost(username, body, tags = [], feedType = null, options = {}) {
    const signatureOptions = await this.resolveSignatureOptions(username, 'short', options);
    const bodyWithSignature = this.appendUserSignatureForContext(
      body,
      username,
      'short',
      signatureOptions.signature,
      signatureOptions.applySignature
    );
    const broadcastBody = this.normalizeBodyForBroadcast(bodyWithSignature);
    const payload = options.targetPost && options.jsonMetadata
      ? { permlink: options.permlink || (options.targetPost.author === 'leothreads' ? this.generateThreadsPermlink(options.targetPost.author) : this.generatePermlink()), targetPost: options.targetPost, jsonMetadata: options.jsonMetadata }
      : await this.buildShortPostPayload(username, broadcastBody, tags, feedType, options.permlink || null);
    const { permlink, targetPost } = payload;
    const isThreadPost = targetPost.author === 'leothreads';
    const title = isThreadPost ? this.buildThreadsTitle(username, broadcastBody) : '';
    const _links = this.extractLinks(broadcastBody);
    const _images = this.extractImages(broadcastBody);
    const jsonMetadata = {
      ...(payload.jsonMetadata || {}),
      ...(_links.length ? { links: _links } : {}),
      ...(_images.length ? { images: _images } : {}),
    };

    const hasSessionPostingKey = !!sessionStorage.getItem('hive_posting_key');
    const shouldUsePostingKey = this.loginMethod === 'postingkey' || (!this.loginMethod && hasSessionPostingKey);

    if (shouldUsePostingKey) {
      // Use posting key to sign and broadcast
      const postingKey = this.getPostingKey();
      
      if (!postingKey) {
        throw new Error('No posting key available');
      }

      try {
        const privateKey = PrivateKey.fromString(postingKey);
        
        const commentOp = ['comment', {
          parent_author: targetPost.author,
          parent_permlink: targetPost.permlink,
          author: username,
          permlink,
          title,
          body: broadcastBody,
          json_metadata: JSON.stringify(jsonMetadata),
        }];
        const ops = isThreadPost
          ? [commentOp, this.buildThreadsCommentOptions(username, permlink)]
          : [commentOp];
        const result = await client.broadcast.sendOperations(ops, privateKey);
        
        this._sendOptimisticPost({
          author: username, permlink, body: broadcastBody, json_metadata: jsonMetadata,
          parent_author: targetPost.author, parent_permlink: targetPost.permlink,
          feed_type: feedType || 'flex', is_comment: false, 
          tx_id: result.id
        });
        
        return { success: true, result, permlink, parent_author: targetPost.author, parent_permlink: targetPost.permlink, json_metadata: jsonMetadata };
      } catch (error) {
        if (this.isAuthError(error)) {
          this.notifyPostingKeyInvalid(username, error.message);
        }
        throw new Error(error.message || 'Failed to post');
      }
    } else {
      // Use Keychain
      return new Promise((resolve, reject) => {
        if (!this.isKeychainInstalled()) {
          reject(new Error('Hive Keychain extension not installed'));
          return;
        }

        if (isThreadPost) {
          const requestBroadcastWithRecovery = (attempt = 1) => {
            window.hive_keychain.requestBroadcast(
              username,
              [['comment', {
                parent_author: targetPost.author,
                parent_permlink: targetPost.permlink,
                author: username,
                permlink,
                title,
                body: broadcastBody,
                json_metadata: JSON.stringify(jsonMetadata),
              }], this.buildThreadsCommentOptions(username, permlink)],
              'posting',
              (response) => {
                if (response?.success) {
                  this._sendOptimisticPost({
                    author: username, permlink, body: broadcastBody, json_metadata: jsonMetadata,
                    parent_author: targetPost.author, parent_permlink: targetPost.permlink,
                    feed_type: feedType || 'flex', is_comment: false,
                    tx_id: response.result?.id || (typeof response.result === 'string' ? response.result : null)
                  });
                  resolve({ success: true, response, permlink, parent_author: targetPost.author, parent_permlink: targetPost.permlink, json_metadata: jsonMetadata });
                  return;
                }
                if ((!response || isPlainEmptyObject(response)) && attempt === 1) {
                  setTimeout(() => requestBroadcastWithRecovery(2), 250);
                  return;
                }
                console.error('Keychain post error', response);
                reject(new Error(extractKeychainErrorMessage(response, 'Failed to post')));
              }
            );
          };
          requestBroadcastWithRecovery(1);
        } else {
          const requestPostWithRecovery = (attempt = 1) => {
            window.hive_keychain.requestPost(
              username,
              '',
              broadcastBody,
              targetPost.permlink,
              targetPost.author,
              JSON.stringify(jsonMetadata),
              permlink,
              '',
              (response) => {
                if (response?.success) {
                  this._sendOptimisticPost({
                    author: username, permlink, body: broadcastBody, json_metadata: jsonMetadata,
                    parent_author: targetPost.author, parent_permlink: targetPost.permlink,
                    feed_type: feedType || 'flex', is_comment: false,
                    tx_id: response.result?.id || (typeof response.result === 'string' ? response.result : null)
                  });
                  resolve({ success: true, response, permlink, parent_author: targetPost.author, parent_permlink: targetPost.permlink, json_metadata: jsonMetadata });
                  return;
                }
                if ((!response || isPlainEmptyObject(response)) && attempt === 1) {
                  setTimeout(() => requestPostWithRecovery(2), 250);
                  return;
                }
                console.error('Keychain post error', response);
                reject(new Error(extractKeychainErrorMessage(response, 'Failed to post')));
              }
            );
          };
          requestPostWithRecovery(1);
        }
      });
    }
  }

  /**
   * Create a comment/reply
   */
  async createComment(username, parentAuthor, parentPermlink, body, rootParentAuthor = null, options = {}) {
    const isThreadReply = rootParentAuthor === 'leothreads' || parentAuthor === 'leothreads';
    const permlink = options.permlink || (isThreadReply ? this.generateThreadsPermlink(parentAuthor) : this.generatePermlink());
    const signatureOptions = await this.resolveSignatureOptions(username, 'comment', options);
    const bodyWithSignature = this.appendUserSignatureForContext(
      body,
      username,
      'comment',
      signatureOptions.signature,
      signatureOptions.applySignature
    );
    const broadcastBody = this.normalizeBodyForBroadcast(bodyWithSignature);
    const title = isThreadReply ? this.buildThreadsTitle(username, broadcastBody) : '';
    const jsonMetadata = this.buildCommentMetadata(username, broadcastBody, parentAuthor, rootParentAuthor, permlink);

    const hasSessionPostingKey = !!sessionStorage.getItem('hive_posting_key');
    const shouldUsePostingKey = this.loginMethod === 'postingkey' || (!this.loginMethod && hasSessionPostingKey);

    if (shouldUsePostingKey) {
      // Use posting key to sign and broadcast
      const postingKey = this.getPostingKey();
      
      if (!postingKey) {
        throw new Error('No posting key available');
      }

      try {
        const privateKey = PrivateKey.fromString(postingKey);
        
        const commentOp = ['comment', {
          parent_author: parentAuthor,
          parent_permlink: parentPermlink,
          author: username,
          permlink,
          title,
          body: broadcastBody,
          json_metadata: JSON.stringify(jsonMetadata),
        }];
        const ops = isThreadReply
          ? [commentOp, this.buildThreadsCommentOptions(username, permlink)]
          : [commentOp];
        const result = await client.broadcast.sendOperations(ops, privateKey);
        
        this._sendOptimisticPost({
          author: username, permlink, body: broadcastBody, json_metadata: jsonMetadata,
          parent_author: parentAuthor, parent_permlink: parentPermlink, is_comment: true,
          tx_id: result.id
        });
        
        return { success: true, result, permlink, parent_author: parentAuthor, parent_permlink: parentPermlink, json_metadata: jsonMetadata };
      } catch (error) {
        // log full error object so developers can inspect RPC response
        console.error('broadcast.comment error', error);
        if (this.isAuthError(error)) {
          this.notifyPostingKeyInvalid(username, error.message);
        }
        throw new Error(error.message || JSON.stringify(error) || 'Failed to comment');
      }
    } else {
      // Use Keychain
      return new Promise((resolve, reject) => {
        if (!this.isKeychainInstalled()) {
          reject(new Error('Hive Keychain extension not installed'));
          return;
        }

        if (isThreadReply) {
          const requestBroadcastWithRecovery = (attempt = 1) => {
            window.hive_keychain.requestBroadcast(
              username,
              [['comment', {
                parent_author: parentAuthor,
                parent_permlink: parentPermlink,
                author: username,
                permlink,
                title,
                body: broadcastBody,
                json_metadata: JSON.stringify(jsonMetadata),
              }], this.buildThreadsCommentOptions(username, permlink)],
              'posting',
              (response) => {
                if (response?.success) {
                  this._sendOptimisticPost({
                    author: username, permlink, body: broadcastBody, json_metadata: jsonMetadata,
                    parent_author: parentAuthor, parent_permlink: parentPermlink, is_comment: true,
                    tx_id: response.result?.id || (typeof response.result === 'string' ? response.result : null)
                  });
                  resolve({ success: true, response, permlink, parent_author: parentAuthor, parent_permlink: parentPermlink, json_metadata: jsonMetadata });
                  return;
                }
                if ((!response || isPlainEmptyObject(response)) && attempt === 1) {
                  setTimeout(() => requestBroadcastWithRecovery(2), 250);
                  return;
                }
                console.error('Keychain comment error', response);
                reject(new Error(extractKeychainErrorMessage(response, 'Failed to comment')));
              }
            );
          };
          requestBroadcastWithRecovery(1);
        } else {
          const requestPostWithRecovery = (attempt = 1) => {
            window.hive_keychain.requestPost(
              username,
              '',
              broadcastBody,
              parentPermlink,
              parentAuthor,
              JSON.stringify(jsonMetadata),
              permlink,
              '',
              (response) => {
                if (response?.success) {
                  this._sendOptimisticPost({
                    author: username, permlink, body: broadcastBody, json_metadata: jsonMetadata,
                    parent_author: parentAuthor, parent_permlink: parentPermlink, is_comment: true,
                    tx_id: response.result?.id || (typeof response.result === 'string' ? response.result : null)
                  });
                  resolve({ success: true, response, permlink, parent_author: parentAuthor, parent_permlink: parentPermlink, json_metadata: jsonMetadata });
                  return;
                }
                if ((!response || isPlainEmptyObject(response)) && attempt === 1) {
                  setTimeout(() => requestPostWithRecovery(2), 250);
                  return;
                }
                console.error('Keychain comment error', response);
                reject(new Error(extractKeychainErrorMessage(response, 'Failed to comment')));
              }
            );
          };
          requestPostWithRecovery(1);
        }
      });
    }
  }

  /**
   * Check if a post exists on-chain
   */
  async checkPostExists(author, permlink) {
    try {
      const content = await client.database.call('get_content', [author, permlink]);
      return !!(content && content.author && content.permlink);
    } catch (error) {
      return false;
    }
  }

  /**
   * Reblog a post as a comment on container
   */
  async reblogAsComment(username, originalAuthor, originalPermlink, originalBody, feedType = 'flex', options = {}) {
    let targetPost;
    
    // Get the appropriate container post
    if (feedType === 'flex') {
      targetPost = { author: 'hive-144726', permlink: 'published-test' };
    } else {
      // Fetch the latest container for other feeds
      targetPost = await this.getLatestContainer(feedType);
      
      if (!targetPost) {
        throw new Error(`No container post found for ${feedType}`);
      }
    }

    const permlink = feedType === 'threads'
      ? this.generateThreadsPermlink(targetPost.author)
      : this.generatePermlink();
    
    // Build metadata based on feed type
    let jsonMetadata;
    
    const signatureOptions = await this.resolveSignatureOptions(username, 'short', options);
    const commentary = typeof options.commentary === 'string' ? options.commentary.trim() : '';
    const embeddedReference = `@${originalAuthor}/${originalPermlink}`;
    const body = commentary ? `${commentary}\n\n${embeddedReference}` : embeddedReference;
    const bodyWithSignature = this.appendUserSignatureForContext(
      body,
      username,
      'short',
      signatureOptions.signature,
      signatureOptions.applySignature
    );
    
    if (feedType === 'threads') {
      // Use leothreads metadata structure
      jsonMetadata = this.buildThreadsMetadata(username, permlink, bodyWithSignature, {
        extra: {
          reblog_of: {
            author: originalAuthor,
            permlink: originalPermlink,
            body: originalBody,
            commentary
          }
        }
      });
    } else {
      // Standard metadata for other feeds
      jsonMetadata = {
        app: `${APP_NAME}/1.0.0`,
        tags: [feedType === 'flex' ? DEFAULT_TAG : feedType, 'reblog'],
        format: 'markdown',
        links: this.extractLinks(bodyWithSignature),
        images: this.extractImages(bodyWithSignature),
        reblog_of: {
          author: originalAuthor,
          permlink: originalPermlink,
          body: originalBody,
          commentary
        }
      };
    }

    const isThreadPost = feedType === 'threads';
    const title = isThreadPost ? this.buildThreadsTitle(username, bodyWithSignature) : '';

    // Support both posting key and Keychain
    const hasSessionPostingKey = !!sessionStorage.getItem('hive_posting_key');
    const shouldUsePostingKey = this.loginMethod === 'postingkey' || (!this.loginMethod && hasSessionPostingKey);

    if (shouldUsePostingKey) {
      const postingKey = this.getPostingKey();

      if (!postingKey) {
        throw new Error('No posting key available');
      }

      try {
        const privateKey = PrivateKey.fromString(postingKey);
        const commentOp = ['comment', {
          parent_author: targetPost.author,
          parent_permlink: targetPost.permlink,
          author: username,
          permlink,
          title,
          body: bodyWithSignature,
          json_metadata: JSON.stringify(jsonMetadata),
        }];
        const ops = isThreadPost
          ? [commentOp, this.buildThreadsCommentOptions(username, permlink)]
          : [commentOp];
        const result = await client.broadcast.sendOperations(ops, privateKey);
        return {
          success: true,
          result,
          permlink,
          parent_author: targetPost.author,
          parent_permlink: targetPost.permlink,
          json_metadata: jsonMetadata
        };
      } catch (error) {
        throw new Error(error.message || 'Failed to reblog');
      }
    } else {
      // Use Keychain
      return new Promise((resolve, reject) => {
        if (!this.isKeychainInstalled()) {
          reject(new Error('Hive Keychain extension not installed'));
          return;
        }

        if (isThreadPost) {
          window.hive_keychain.requestBroadcast(
            username,
            [['comment', {
              parent_author: targetPost.author,
              parent_permlink: targetPost.permlink,
              author: username,
              permlink,
              title,
              body: bodyWithSignature,
              json_metadata: JSON.stringify(jsonMetadata),
            }], this.buildThreadsCommentOptions(username, permlink)],
            'posting',
            (response) => {
              if (response.success) {
                resolve(response);
              } else {
                reject(new Error(response.message || 'Failed to reblog'));
              }
            }
          );
        } else {
          window.hive_keychain.requestPost(
            username,
            '',
            bodyWithSignature,
            targetPost.permlink,
            targetPost.author,
            JSON.stringify(jsonMetadata),
            permlink,
            '',
            (response) => {
              if (response.success) {
                resolve(response);
              } else {
                reject(new Error(response.message || 'Failed to reblog'));
              }
            }
          );
        }
      });
    }
  }

  /**
   * Vote on a post or comment
   */
  async vote(username, author, permlink, weight = 10000) {
    // Validate and sanitize parameters
    if (!username || !author || !permlink) {
      throw new Error('Username, author, and permlink are required');
    }

    // Ensure all string fields are properly formatted
    // Account names and voter must be lowercase
    const voter = String(username).trim().toLowerCase();
    const postAuthor = String(author).trim().toLowerCase();
    // Permlink should NOT be lowercased as it's case-sensitive
    const postPermlink = String(permlink).trim();
    
    // Ensure weight is a proper integer and within valid Hive range [-10000, 10000].
    // 0 (remove vote) is valid on-chain.
    let voteWeight = Math.round(Number(weight));
    if (!Number.isFinite(voteWeight)) {
      console.warn('hiveService.vote received invalid weight, defaulting to 10000');
      voteWeight = 10000;
    }
    if (voteWeight > 10000) voteWeight = 10000;
    if (voteWeight < -10000) voteWeight = -10000;
    
    console.log('Vote parameters:', { voter, postAuthor, postPermlink, voteWeight });
    
    if (this.loginMethod === 'postingkey' || sessionStorage.getItem('hive_posting_key')) {
      // Use posting key to sign and broadcast
      const postingKey = this.getPostingKey();
      
      if (!postingKey) {
        throw new Error('No posting key available');
      }

      try {
        const privateKey = PrivateKey.fromString(postingKey);
        
        const vote = {
          voter: voter,
          author: postAuthor,
          permlink: postPermlink,
          weight: voteWeight
        };

        console.log('Broadcasting vote transaction:', vote);
        const result = await client.broadcast.vote(vote, privateKey);
        
        // Dispatch optimistic update to backend
        this._sendOptimisticVote(postAuthor, postPermlink, voteWeight);
        
        return { success: true, result };
      } catch (error) {
        console.error('Vote broadcast error:', error);
        console.error('Error details:', error.toString());
        if (this.isAuthError(error)) {
          this.notifyPostingKeyInvalid(voter, error.message);
        }
        throw new Error(error.message || 'Error while broadcasting the transaction');
      }
    } else {
      // Use Keychain
      return new Promise((resolve, reject) => {
        if (!this.isKeychainInstalled()) {
          reject(new Error('Hive Keychain extension not installed'));
          return;
        }

        const voteData = {
          voter,
          postAuthor,
          postPermlink,
          voteWeight,
          appName: APP_NAME
        };
        console.log('=== KEYCHAIN VOTE DATA ===');
        console.log(JSON.stringify(voteData, null, 2));
        console.log('Voter length:', voter.length);
        console.log('Author length:', postAuthor.length);
        console.log('Permlink length:', postPermlink.length);
        console.log('========================');

        const verifyVoteApplied = async () => {
          try {
            const activeVotes = await client.database.call('get_active_votes', [postAuthor, postPermlink]);
            if (!Array.isArray(activeVotes)) return false;
            const matched = activeVotes.find((v) => String(v?.voter || '').toLowerCase() === voter);
            if (!matched) return false;
            const chainPercent = Number(matched.percent);
            return Number.isFinite(chainPercent) && chainPercent === voteWeight;
          } catch (_err) {
            return false;
          }
        };

        const requestVoteWithRecovery = (attempt = 1) => {
          window.hive_keychain.requestVote(
            voter,
            postPermlink,
            postAuthor,
            voteWeight,
            async (response) => {
              console.log('Keychain response:', response);
              if (response?.success) {
                this._sendOptimisticVote(postAuthor, postPermlink, voteWeight);
                resolve(response);
                return;
              }

              const receivedEmptyPayload = !response || isPlainEmptyObject(response);
              if (receivedEmptyPayload && attempt === 1) {
                console.warn('Keychain returned empty vote response. Retrying once...');
                setTimeout(() => requestVoteWithRecovery(2), 250);
                return;
              }

              // Keychain sometimes shows a generic "Oops!" even when the vote lands on-chain.
              // Verify chain state once before surfacing an error.
              const genericFailure = String(response?.message || response?.error || '')
                .toLowerCase()
                .includes('oops');

              if (receivedEmptyPayload || genericFailure) {
                const applied = await verifyVoteApplied();
                if (applied) {
                  this._sendOptimisticVote(postAuthor, postPermlink, voteWeight);
                  resolve({ success: true, recovered: true, source: 'chain-verification', response });
                  return;
                }
              }

              const errorMessage = extractKeychainErrorMessage(
                response,
                'Failed to vote via Hive Keychain'
              );
              console.error('Keychain vote error:', errorMessage, response?.error || response);
              reject(new Error(errorMessage));
            }
          );
        };

        requestVoteWithRecovery(1);
      });
    }
  }

  /**
   * Follow an account
   */
  async follow(follower, following) {
    const jsonPayload = [
      'follow',
      {
        follower: follower,
        following: following,
        what: ['blog']
      }
    ];

    if (this.loginMethod === 'postingkey') {
      const postingKey = this.getPostingKey();
      if (!postingKey) throw new Error('No posting key available');
      try {
        const privateKey = PrivateKey.fromString(postingKey);
        const op = ['custom_json', {
          id: 'follow',
          required_auths: [],
          required_posting_auths: [follower],
          json: JSON.stringify(jsonPayload)
        }];
        return await client.broadcast.sendOperations([op], privateKey);
      } catch (err) {
        throw new Error(err.message || 'Failed to follow');
      }
    }

    return new Promise((resolve, reject) => {
      if (!this.isKeychainInstalled()) {
        reject(new Error('Hive Keychain extension not installed'));
        return;
      }
      window.hive_keychain.requestCustomJson(
        follower,
        'follow',
        'Posting',
        JSON.stringify(jsonPayload),
        'Follow',
        (response) => {
          if (response.success) {
            resolve(response);
          } else {
            reject(new Error(response.message || 'Failed to follow'));
          }
        }
      );
    });
  }

  /**
   * Unfollow an account
   */
  async unfollow(follower, following) {
    const jsonPayload = [
      'follow',
      {
        follower: follower,
        following: following,
        what: []
      }
    ];

    if (this.loginMethod === 'postingkey') {
      const postingKey = this.getPostingKey();
      if (!postingKey) throw new Error('No posting key available');
      try {
        const privateKey = PrivateKey.fromString(postingKey);
        const op = ['custom_json', {
          id: 'follow',
          required_auths: [],
          required_posting_auths: [follower],
          json: JSON.stringify(jsonPayload)
        }];
        return await client.broadcast.sendOperations([op], privateKey);
      } catch (err) {
        throw new Error(err.message || 'Failed to unfollow');
      }
    }

    return new Promise((resolve, reject) => {
      if (!this.isKeychainInstalled()) {
        reject(new Error('Hive Keychain extension not installed'));
        return;
      }
      window.hive_keychain.requestCustomJson(
        follower,
        'follow',
        'Posting',
        JSON.stringify(jsonPayload),
        'Unfollow',
        (response) => {
          if (response.success) {
            resolve(response);
          } else {
            reject(new Error(response.message || 'Failed to unfollow'));
          }
        }
      );
    });
  }

  /**
   * Reblog a post
   */
  async reblog(username, author, permlink) {
    const payload = [
      'reblog',
      {
        account: username,
        author: author,
        permlink: permlink
      }
    ];

    return this.broadcastCustomJson(username, 'reblog', payload);
  }

  /**
   * Get wallet information for a user
   */
  async getWalletInfo(username) {
    try {
      const account = await client.database.getAccounts([username]);
      
      if (!account || account.length === 0) {
        throw new Error('Account not found');
      }

      const accountData = account[0];
      const tokens = [];

      // HIVE token
      const hiveBalance = parseFloat(accountData.balance.split(' ')[0]);

      // Use dynamic global props to convert VESTS -> HP correctly
      const dyn = await client.database.getDynamicGlobalProperties();
      const total_vesting_shares = parseFloat(dyn.total_vesting_shares.split(' ')[0]) || 0;
      const total_vesting_fund_hive = parseFloat(dyn.total_vesting_fund_hive.split(' ')[0]) || 0;

      const vestsToHP = (vests) => {
        const v = parseFloat(vests || 0);
        if (!total_vesting_shares || !total_vesting_fund_hive) return 0;
        return (v * total_vesting_fund_hive) / total_vesting_shares;
      };

      const hiveVests = parseFloat(accountData.vesting_shares.split(' ')[0]) || 0; // includes received delegations
      const delegatedVests = parseFloat(accountData.delegated_vesting_shares?.split(' ')[0] || 0) || 0; // delegated out
      const receivedVests = parseFloat(accountData.received_vesting_shares?.split(' ')[0] || 0) || 0; // delegated in

      const totalHP = vestsToHP(hiveVests);
      const delegatedOutHP = vestsToHP(delegatedVests);
      const receivedHP = vestsToHP(receivedVests);
      const ownVests = Math.max(0, hiveVests - receivedVests); // original vests owned (excluding delegations received)
      const ownHP = vestsToHP(ownVests);
      const availableHP = Math.max(0, totalHP - delegatedOutHP);

      const hiveSavings = parseFloat(accountData.savings_balance?.split(' ')[0] || 0);

      tokens.push({
        ticker: 'HIVE',
        liquid: hiveBalance.toFixed(6),
        staked: ownHP.toFixed(6), // user's own HP (excludes received delegations)
        delegated: delegatedOutHP.toFixed(6), // delegated out (HP)
        received: receivedHP.toFixed(6), // delegated in (HP)
        total_hp: totalHP.toFixed(6),
        available_hp: availableHP.toFixed(6),
        savings: hiveSavings.toFixed(6)
      });

      // HBD token
      const hbdBalance = parseFloat(accountData.hbd_balance.split(' ')[0]);
      const hbdSavings = parseFloat(accountData.savings_hbd_balance?.split(' ')[0] || 0);
      tokens.push({
        ticker: 'HBD',
        liquid: hbdBalance.toFixed(3),
        staked: '0.000',
        delegated: '0.000',
        savings: hbdSavings.toFixed(3)
      });

      // Try to fetch Hive Engine tokens (layer 2 tokens)
      try {
        const engineTokens = await this.getHiveEngineTokens(username);
        tokens.push(...engineTokens);
      } catch (err) {
        console.error('Error fetching Hive Engine tokens:', err);
      }

      return {
        tokens,
        dynamic_props: {
          total_vesting_shares: dyn.total_vesting_shares,
          total_vesting_fund_hive: dyn.total_vesting_fund_hive
        }
      };
    } catch (error) {
      console.error('Error fetching wallet info:', error);
      throw error;
    }
  }

  /**
   * Get Hive Engine tokens (layer 2 tokens like LEO, PIZZA, etc.)
   */
  async getHiveEngineTokens(username) {
    try {
      // Try primary API endpoint
      let response = await fetch('https://api.hive-engine.com/rpc/contracts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          jsonrpc: '2.0',
          method: 'find',
          params: {
            contract: 'tokens',
            table: 'balances',
            query: {
              account: username
            },
            limit: 1000,
            offset: 0
          },
          id: 1
        })
      });

      let data = await response.json();
      
      // If primary fails, try backup endpoint
      if (!data.result) {
        console.log('Trying backup Hive Engine API...');
        response = await fetch('https://engine.rishipanthee.com/contracts', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'find',
            params: {
              contract: 'tokens',
              table: 'balances',
              query: {
                account: username
              },
              limit: 1000,
              offset: 0
            },
            id: 1
          })
        });
        data = await response.json();
      }

      const tokens = [];

      if (data.result && Array.isArray(data.result)) {
        console.log(`Found ${data.result.length} Hive Engine token balances`);
        for (const token of data.result) {
          const balance = parseFloat(token.balance || 0);
          const staked = parseFloat(token.stake || token.staked || 0);
          const delegatedOut = parseFloat(token.delegationsOut || token.delegated || 0);
          const received = parseFloat(token.delegationsIn || token.received || 0);
          const savings = parseFloat(token.savings || 0);

          // Include tokens that have any non-zero value (liquid, staked, delegated out, received, savings)
          if (balance > 0 || staked > 0 || delegatedOut > 0 || received > 0 || savings > 0) {
            tokens.push({
              ticker: (token.symbol || token.token || '').toString().toUpperCase(),
              liquid: balance.toFixed(6),
              staked: staked.toFixed(6),
              delegated: delegatedOut.toFixed(6),
              received: received.toFixed(6),
              savings: savings.toFixed(6)
            });
          }
        }
        console.log(`Returning ${tokens.length} tokens with balances`);
      } else {
        console.log('No Hive Engine tokens found or API returned no results');
      }

      return tokens;
    } catch (error) {
      console.error('Error fetching Hive Engine tokens:', error);
      return [];
    }
  }


  /**
   * Generate a unique permlink
   */
  generatePermlink() {
    const random = Math.random().toString(36).substring(2, 8);
    const timestamp = Date.now().toString(36);
    return `flexus-${timestamp}-${random}`;
  }

  generateThreadsPermlink(parentAuthor) {
    let suffix = '';
    while (suffix.length < 9) {
      suffix += Math.random().toString(36).substring(2);
    }
    return `re-${parentAuthor}-${suffix.substring(0, 5)}fl${suffix.substring(5, 7)}`;
  }

  /**
   * Generate the correct permlink for a reply, using the re- thread format
   * when the parent or root is a leothreads post, otherwise the standard format.
   */
  generatePermlinkForReply(parentAuthor, rootParentAuthor = null) {
    const isThread = parentAuthor === 'leothreads' || rootParentAuthor === 'leothreads';
    return isThread ? this.generateThreadsPermlink(parentAuthor) : this.generatePermlink();
  }

  /**
   * Look up the human-readable title for a community slug ("hive-123456").
   * Returns the title string or the slug if unable to fetch.
   * Uses the existing postsApi.getCommunityInfo helper.
   */
  async getCommunityTitle(slug) {
    if (!slug || typeof slug !== 'string') return slug;
    try {
      const info = await postsApi.getCommunityInfo(slug);
      if (info && info.title && info.title !== slug) {
        return info.title;
      }
    } catch (err) {
      console.error('Error looking up community title for', slug, err);
    }
    // if API returned the slug or failed, try direct bridge call
    try {
      const community = await client.call('bridge', 'get_community', {
        name: slug,
        observer: null
      });
      if (community && community.title && community.title !== slug) {
        return community.title;
      }
    } catch (err) {
      console.error('Error fetching community directly from chain for', slug, err);
    }
    return slug;
  }

  /**
   * Build comment_options operation based on reward option and beneficiaries
   * @param {string} username - Author username
   * @param {string} permlink - Post permlink
   * @param {string|number|null} rewardOption - Reward distribution option
   * @param {Array} beneficiaries - Array of beneficiary objects {account, weight}
   * @returns {object|null} comment_options object or null if not needed
   */
  buildCommentOptions(username, permlink, rewardOption = null, beneficiaries = []) {
    if (rewardOption === null && (!Array.isArray(beneficiaries) || beneficiaries.length === 0)) {
      return null;
    }

    // Translate reward option into comment_options values per Hive protocol
    const num = parseInt(rewardOption, 10);
    let percent_hbd = 10000; // Default: 10000 = 100% of liquid portion as HBD
    let max_accepted_payout = '1000000.000 HBD';
    let allow_curation_rewards = true;

    // Handle special cases
    if (num === 100) {
      // 100% Hive Power
      percent_hbd = 0;
    } else if (num === 0) {
      // Decline rewards
      max_accepted_payout = '0.000 HBD';
      allow_curation_rewards = false;
    }
    // num === 50 or default uses percent_hbd = 10000 (50% HBD / 50% HP)

    const commentOptions = {
      author: username,
      permlink,
      max_accepted_payout,
      percent_hbd,
      allow_votes: true,
      allow_curation_rewards,
      extensions: []
    };

    // Build beneficiaries list
    const customBeneficiaries = Array.isArray(beneficiaries) ? beneficiaries : [];
    
    // Map reward option codes to account names
    const rewardOptionAccounts = {
      '-1': 'null',           // Burn
      '-2': 'hive.fund',      // Donate to Hive Fund
      '-3': 'reward.app',     // Use @reward.app
      '-4': 'commentrewarder' // Use @commentrewarder
    };

    // Check if reward option includes a beneficiary account
    const rewardAccount = rewardOptionAccounts[num.toString()];
    
    if (customBeneficiaries.length > 0 || rewardAccount) {
      // Start with custom beneficiaries
      const beneficiariesMap = {};
      
      // Add custom beneficiaries
      customBeneficiaries.forEach(b => {
        if (b.account && b.weight) {
          beneficiariesMap[b.account] = (beneficiariesMap[b.account] || 0) + b.weight;
        }
      });

      // If reward option is a beneficiary type, add it with remaining weight
      if (rewardAccount) {
        // Calculate total weight of custom beneficiaries
        const customWeight = Object.values(beneficiariesMap).reduce((sum, w) => sum + w, 0);
        
        // Remaining weight for the reward option account (ensure it doesn't go below 0)
        const remainingWeight = Math.max(0, 10000 - customWeight);
        
        if (remainingWeight > 0) {
          // Add to map (or increase if already exists in custom)
          beneficiariesMap[rewardAccount] = (beneficiariesMap[rewardAccount] || 0) + remainingWeight;
        }
      }

      // Convert map to array and sort alphabetically by account name
      const sortedBeneficiaries = Object.entries(beneficiariesMap)
        .map(([account, weight]) => ({ account, weight }))
        .sort((a, b) => a.account.localeCompare(b.account));

      // Validate total weight doesn't exceed 10000
      const totalWeight = sortedBeneficiaries.reduce((sum, b) => sum + b.weight, 0);
      if (totalWeight > 10000) {
        console.warn('Total beneficiary weight exceeds 10000:', totalWeight);
      }

      if (sortedBeneficiaries.length > 0) {
        commentOptions.extensions.push([0, { beneficiaries: sortedBeneficiaries }]);
      }
    }

    return commentOptions;
  }

  /**
   * Create an article (long-form post with title and community)
   * @param {string} title
   * @param {string} body
   * @param {object} metadata
   * @param {string|null} permlinkOverride - if provided, use this permlink instead of generating one
   */
  async createArticle(title, body, metadata = {}, permlinkOverride = null, rewardOption = null, beneficiaries = []) {
    const username = localStorage.getItem('hive_username');
    if (!username) {
      throw new Error('User not logged in');
    }

    const signatureOptions = await this.resolveSignatureOptions(username, 'article');
    const permlink = permlinkOverride || this.generatePermlink();
    const bodyWithSignature = this.appendUserSignatureForContext(
      body,
      username,
      'article',
      signatureOptions.signature,
      signatureOptions.applySignature
    );
    const broadcastBody = this.normalizeBodyForBroadcast(bodyWithSignature);
    
    // Ensure metadata has required fields for articles
    const jsonMetadata = {
      app: metadata.app || 'flexus/0.1',
      format: metadata.format || 'markdown',
      tags: metadata.tags || [],
      ...metadata
    };

    if (!jsonMetadata.links) {
      jsonMetadata.links = this.extractLinks(broadcastBody);
    }
    if (!jsonMetadata.images && !jsonMetadata.image) {
      const extractedImages = this.extractImages(broadcastBody);
      if (extractedImages.length === 1) {
        jsonMetadata.image = extractedImages;
      } else if (extractedImages.length > 1) {
        jsonMetadata.images = extractedImages;
      }
    }

    const featuredImage =
      (Array.isArray(jsonMetadata.images) && jsonMetadata.images[0]) ||
      (Array.isArray(jsonMetadata.image) && jsonMetadata.image[0]) ||
      jsonMetadata.featured_image ||
      jsonMetadata.thumbnail ||
      '';

    if (featuredImage) {
      jsonMetadata.featured_image = featuredImage;
      jsonMetadata.thumbnail = featuredImage;
      // If no image array exists yet, add a single-item one
      if (!jsonMetadata.images && !jsonMetadata.image) {
        jsonMetadata.image = [featuredImage];
      }
    }

    // operations array may include comment and options
    const ops = [];

    const hasSessionPostingKey = !!sessionStorage.getItem('hive_posting_key');
    const shouldUsePostingKey = this.loginMethod === 'postingkey' || (!this.loginMethod && hasSessionPostingKey);

    if (shouldUsePostingKey) {
      // Use posting key
      const postingKey = this.getPostingKey();
      
      if (!postingKey) {
        throw new Error('No posting key available');
      }
      
      try {
        const privateKey = PrivateKey.fromString(postingKey);
        
        const comment = {
          parent_author: '',
          parent_permlink: jsonMetadata.tags && jsonMetadata.tags[0] ? jsonMetadata.tags[0] : 'flexus',
          author: username,
          permlink: permlink,
          title: title,
          body: broadcastBody,
          json_metadata: JSON.stringify(jsonMetadata)
        };
        ops.push(['comment', comment]);

        // Add comment_options if reward distribution or beneficiaries are specified
        const commentOptions = this.buildCommentOptions(username, permlink, rewardOption, beneficiaries);
        if (commentOptions) {
          ops.push(['comment_options', commentOptions]);
        }

        const result = await client.broadcast.sendOperations(ops, privateKey);
        return { success: true, result, permlink };
      } catch (error) {
        if (this.isAuthError(error)) {
          this.notifyPostingKeyInvalid(username, error.message);
        }
        throw new Error(error.message || 'Failed to create article');
      }
    } else {
      // Use Keychain
      return new Promise((resolve, reject) => {
        if (!this.isKeychainInstalled()) {
          reject(new Error('Hive Keychain extension not installed'));
          return;
        }

        const parentPermlink = jsonMetadata.tags && jsonMetadata.tags[0] ? jsonMetadata.tags[0] : 'flexus';
        const comment = {
          parent_author: '',
          parent_permlink: parentPermlink,
          author: username,
          permlink: permlink,
          title: title,
          body: broadcastBody,
          json_metadata: JSON.stringify(jsonMetadata)
        };

        const ops = [['comment', comment]];

        // Add comment_options if reward distribution or beneficiaries are specified
        const commentOptions = this.buildCommentOptions(username, permlink, rewardOption, beneficiaries);
        if (commentOptions) {
          ops.push(['comment_options', commentOptions]);
        }

        window.hive_keychain.requestBroadcast(
          username,
          ops,
          'posting',
          (response) => {
            if (response.success) {
              resolve({ success: true, response, permlink });
            } else {
              reject(new Error(response.message || 'Failed to create article'));
            }
          }
        );
      });
    }
  }

  /**
   * Edit an existing post/article/comment on Hive by broadcasting a comment
   * operation with the same author+permlink (Hive UPDATE semantics).
   * NOTE: comment_options is intentionally omitted — re-broadcasting it on an
   * edited post that already has votes will fail on-chain.
   */
  async editPost(author, permlink, title, body, parentAuthor, parentPermlink, jsonMetadata) {
    const isArticle = !parentAuthor;
    let broadcastBody;

    if (isArticle) {
      const signatureOptions = await this.resolveSignatureOptions(author, 'article');
      const bodyWithSignature = this.appendUserSignatureForContext(
        body, author, 'article',
        signatureOptions.signature, signatureOptions.applySignature
      );
      broadcastBody = this.normalizeBodyForBroadcast(bodyWithSignature);
    } else {
      broadcastBody = this.normalizeBodyForBroadcast(body);
    }

    const ops = [['comment', {
      parent_author: parentAuthor || '',
      parent_permlink: parentPermlink || '',
      author,
      permlink,
      title: title || '',
      body: broadcastBody,
      json_metadata: JSON.stringify(jsonMetadata),
    }]];
    // No comment_options — must not re-broadcast on edits

    const hasSessionPostingKey = !!sessionStorage.getItem('hive_posting_key');
    const shouldUsePostingKey = this.loginMethod === 'postingkey' || (!this.loginMethod && hasSessionPostingKey);

    if (shouldUsePostingKey) {
      const postingKey = this.getPostingKey();
      if (!postingKey) throw new Error('No posting key available');
      try {
        const privateKey = PrivateKey.fromString(postingKey);
        const result = await client.broadcast.sendOperations(ops, privateKey);
        return { success: true, result, permlink };
      } catch (error) {
        if (this.isAuthError(error)) {
          this.notifyPostingKeyInvalid(author, error.message);
        }
        throw new Error(error.message || 'Failed to edit post');
      }
    } else {
      return new Promise((resolve, reject) => {
        if (!this.isKeychainInstalled()) {
          reject(new Error('Hive Keychain extension not installed'));
          return;
        }
        window.hive_keychain.requestBroadcast(
          author,
          ops,
          'posting',
          (response) => {
            if (response.success) {
              resolve({ success: true, response, permlink });
            } else {
              reject(new Error(response.message || 'Failed to edit post'));
            }
          }
        );
      });
    }
  }

  /**
   * Subscribe to a Hive community.
   * Broadcasts a custom_json with id "community" via Keychain or posting key.
   */
  async subscribeCommunity(username, communityName) {
    const community = /^\d+$/.test(communityName) ? `hive-${communityName}` : communityName;
    const payload = ['subscribe', { community }];
    return this.broadcastCustomJson(username, 'community', payload);
  }

  /**
   * Unsubscribe from a Hive community.
   */
  async unsubscribeCommunity(username, communityName) {
    const community = /^\d+$/.test(communityName) ? `hive-${communityName}` : communityName;
    const payload = ['unsubscribe', { community }];
    return this.broadcastCustomJson(username, 'community', payload);
  }
}

const hiveService = new HiveService();
hiveService.client = client;
export default hiveService;
